import { useTranslation } from "react-i18next";
import Dialog from "../../../components/Dialog";
import SubmitButton from "../../../components/SubmitButton";
import { useConfirmationWithIntl } from "../../../components/ConfirmationDialog";
import useReload from "../../../hooks/useReload";
import { cx } from "../../../react-helpers/css";
import { Organization } from "../../../services/organization/organizationModel";
import {
  PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS,
  PremiumSponsorship,
} from "../../../services/premiumSponsorship/premiumSponsorshipModel";
import { premiumSponsorshipService } from "../../../services/premiumSponsorship/premiumSponsorshipService";
import { toastsWithIntl } from "../../../services/toastService";

interface Props {
  onClose(): void;
  organization: Organization;
  sponsorship: PremiumSponsorship;
}

const {
  resendPremiumSponsorshipInvitation,
  revokePremiumSponsorship,
  deletePremiumSponsorship,
} = premiumSponsorshipService();

const ManagePremiumSponsorshipDialog = ({
  onClose,
  organization,
  sponsorship,
}: Props) => {
  const { t } = useTranslation(["organization"]);
  const { toastSuccess } = toastsWithIntl(["organization"]);
  const { confirm } = useConfirmationWithIntl(["organization"]);
  const reload = useReload();

  return (
    <Dialog onClose={onClose} className="--branded">
      <>
        <div className="popup_head">
          <div className="popup_title">
            {t("organization:premium-sponsorship.manage.TITLE")}
          </div>
        </div>
        <div className="popup_body">
          <div className="cblock">
            <span
              className={cx([
                "user-status-chip",
                sponsorship.status === "accepted" && "--active",
                sponsorship.status === "declined" && "--declined",
                sponsorship.status === "revoked" && "--revoked",
              ])}
            >
              {t(PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS[sponsorship.status])}
            </span>
          </div>

          <div className="cblock">
            <input
              type="text"
              className="input"
              value={sponsorship.email}
              disabled
            />
          </div>

          {sponsorship.status === "invited" && (
            <div className="cblock">
              <div className="lrow --center--v">
                <div>
                  <SubmitButton
                    className="link --light"
                    type="button"
                    onClick={() => {
                      return resendPremiumSponsorshipInvitation(
                        organization.id,
                        sponsorship.id,
                      );
                    }}
                  >
                    {t(
                      "organization:premium-sponsorship.manage.RESEND_INVITATION",
                    )}
                  </SubmitButton>
                  {sponsorship.token && (
                    <>
                      {" | "}
                      <button
                        className="link --light"
                        type="button"
                        onClick={(ev) => {
                          if (ev.isTrusted)
                            void navigator.clipboard
                              .writeText(
                                `${location.origin}/premium-sponsorship/${sponsorship.token}`,
                              )
                              .then(() => {
                                toastSuccess(
                                  "organization:premium-sponsorship.manage.LINK_COPIED",
                                  {
                                    id: "sponsorship-invitation-link",
                                  },
                                );
                              });
                        }}
                      >
                        {t(
                          "organization:premium-sponsorship.manage.COPY_INVITATION_LINK",
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="form_footer --txt--center">
            <div className="cblock lrow --center --gap--s">
              {(sponsorship.status === "invited" ||
                sponsorship.status === "accepted") && (
                <button
                  className="btn"
                  type="button"
                  onClick={() => {
                    confirm(
                      "organization:premium-sponsorship.confirm-revoke.MESSAGE",
                      () =>
                        revokePremiumSponsorship(
                          organization.id,
                          sponsorship.id,
                        ).then(() => {
                          reload();
                          onClose();
                        }),
                      true,
                    );
                  }}
                >
                  {t("organization:premium-sponsorship.manage.REVOKE")}
                </button>
              )}
              <button
                className="btn--danger"
                type="button"
                onClick={() => {
                  confirm(
                    "organization:premium-sponsorship.confirm-delete.MESSAGE",
                    () =>
                      deletePremiumSponsorship(
                        organization.id,
                        sponsorship.id,
                      ).then(() => {
                        reload();
                        onClose();
                      }),
                    true,
                    {
                      email: sponsorship.email,
                    },
                  );
                }}
              >
                {t("organization:premium-sponsorship.manage.DELETE")}
              </button>
            </div>
          </div>
        </div>
      </>
    </Dialog>
  );
};

export default ManagePremiumSponsorshipDialog;
